import {
  readUtmCookie,
  UTM_FIRST_TOUCH_COOKIE,
  UTM_LAST_TOUCH_COOKIE,
  UTM_KEYS,
  type UtmCookieValue
} from '@lib/attribution/cookies';
import { posthog } from '@lib/posthog';

/**
 * FFH-1899 — split PostHog reporting by campaign UTM.
 *
 * The `go.` redirect strips `utm_*` params from the URL before any JS runs
 * (writing them to first-party cookies), so PostHog's built-in URL-based UTM
 * capture never sees them. Instead the UTMs are read from the cookies and
 * registered as super properties so they attach to every event:
 *
 *   - last-touch (primary, per Matt) → the standard `utm_*` keys PostHog's
 *     own campaign reporting reads natively
 *   - first-touch (secondary)       → `first_utm_*`
 */

const mapTouch = (
  parsed: UtmCookieValue | null,
  prefix: string
): Record<string, string> => {
  if (!parsed) return {};
  const out: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = parsed[key];
    if (value) out[`${prefix}${key}`] = value;
  }
  return out;
};

/** Pure builder: `{utm_*}` from last-touch + `{first_utm_*}` from first-touch. */
export const buildUtmSuperProperties = (): Record<string, string> => ({
  ...mapTouch(readUtmCookie(UTM_LAST_TOUCH_COOKIE), ''),
  ...mapTouch(readUtmCookie(UTM_FIRST_TOUCH_COOKIE), 'first_')
});

/** Every super-property key this module owns — both touch families. */
const ALL_UTM_PROPERTY_KEYS = [
  ...UTM_KEYS,
  ...UTM_KEYS.map((key) => `first_${key}`)
];

/**
 * Registers the UTM split as PostHog super properties. Idempotent and safe to
 * re-run — called after init and again on consent grant, because a consent
 * revoke calls `posthog.reset()`, which wipes registered super properties.
 * Registering itself captures nothing, so it is safe pre-consent (capture
 * stays opted out and persistence is in-memory until consent).
 *
 * Keys absent from the fresh cookie read are explicitly UNREGISTERED:
 * `posthog.register` merges and never deletes, and registered properties
 * survive in localStorage indefinitely — without the clear, an expired cookie
 * (30-day) or a later narrower last-touch would leave stale campaign values
 * on every subsequent event.
 */
export const registerUtmSuperProperties = (): void => {
  if (typeof window === 'undefined') return;

  const properties = buildUtmSuperProperties();

  for (const key of ALL_UTM_PROPERTY_KEYS) {
    if (!(key in properties)) posthog.unregister(key);
  }

  if (Object.keys(properties).length === 0) return;
  posthog.register(properties);
};
